import { r as registerInstance, h, a as Host, d as getElement } from './index-BntG-4Sv.js';
import { d as debounce } from './function-utils-D1ZpXQQS.js';
import { service, AuthenticationService, AuthorizationService, NavigationContextService, AuthenticationStorageService, RuntimeConfigurationContextService, EventService, ApplicationLifecycleContextService, StorageKey, SubscriptionList, LocalStorageSubscriptionHandlerService, WindowService, navigate, SecurityContextService, EventName, MainMenuExtensionPoint } from '@ontotext/workbench-api';
import { T as TranslationService } from './translation.service-FB05Sh97.js';

const RESPONSIVE_BREAKPOINTS = {
    MD: 768};
/**
 * The width of the window in pixels at which the navbar should collapse.
 */
const WINDOW_WIDTH_FOR_COLLAPSED_NAVBAR = RESPONSIVE_BREAKPOINTS.MD;

const ontoLayoutCss = ":host{display:block;height:100%}.wb-layout{display:grid;grid-template:\"header header\" auto \"nav main\" 1fr \"nav footer\" auto/15rem minmax(200px, 1fr);min-height:100vh;margin:0;}.wb-layout.expanded{grid-template-columns:4.375rem 1fr}.wb-layout.is-embedded{grid-template-columns:1fr;grid-template-areas:\"main\";padding-top:1rem}.wb-layout.hide-navbar{grid-template:\"header\" auto \"main\" 1fr \"footer\" auto/1fr;}.wb-layout.hide-navbar.expanded{grid-template-columns:1fr}.wb-layout main .main-container{margin-left:0}.wb-layout .main-slot-wrapper .loader-wrapper{flex-grow:1;display:flex;align-items:center;justify-content:center}.wb-layout .main-slot-wrapper.loading{display:flex;flex-direction:column}.wb-layout .main-slot-wrapper.loading .page-content{visibility:hidden}.wb-layout .default-slot-wrapper{display:none}.wb-layout .wb-header{position:sticky;top:0;z-index:999}.wb-layout nav{grid-area:nav}.wb-layout header{grid-area:header}.wb-layout main{grid-area:main}.wb-layout footer{grid-area:footer}";

const OntoLayout = class {
    // ========================
    // Lifecycle methods
    // ========================
    constructor(hostRef) {
        registerInstance(this, hostRef);
        // ========================
        // Services
        // ========================
        this.authenticationService = service(AuthenticationService);
        this.authorizationService = service(AuthorizationService);
        this.navigationContextService = service(NavigationContextService);
        this.authStorageService = service(AuthenticationStorageService);
        this.runtimeConfigurationContextService = service(RuntimeConfigurationContextService);
        this.eventService = service(EventService);
        this.applicationLifecycleContextService = service(ApplicationLifecycleContextService);
        this.fullJwtKey = `${StorageKey.GLOBAL_NAMESPACE}.${this.authStorageService.NAMESPACE}.${this.authStorageService.jwtKey}`;
        this.fullAuthenticatedKey = `${StorageKey.GLOBAL_NAMESPACE}.${this.authStorageService.NAMESPACE}.${this.authStorageService.authenticatedKey}`;
        this.authEvents = [this.fullJwtKey, this.fullAuthenticatedKey];
        this.isLowResolution = false;
        this.showHeader = this.isAuthenticatedFully();
        this.showNavbar = false;
        this.isEmbedded = false;
        this.loading = true;
        // ========================
        // Private
        // ========================
        /**
         * List of subscriptions to manage component lifecycle
         * */
        this.subscriptions = new SubscriptionList();
        this.isNavbarCollapsed = false;
        this.handleStorageChange = (event) => {
            const localStorageSubscriptionHandlerService = service(LocalStorageSubscriptionHandlerService);
            localStorageSubscriptionHandlerService.handleStorageChange(event);
            this.handleAuthChange(event.key);
        };
        this.windowResizeObserver = debounce(() => this.windowResizeHandler(), 50);
        WindowService.getWindow().addEventListener('storage', this.handleStorageChange);
    }
    componentDidLoad() {
        this.windowResizeHandler();
    }
    connectedCallback() {
        this.subscribeToSecurityChanges();
        this.updateVisibility();
        this.subscribeToNavigationEnd();
        this.subscribeToRuntimeConfigurationChanges();
        this.subscribeToApplicationChange();
    }
    /**
     * Lifecycle method called when the component is about to be removed from the DOM.
     * Unsubscribes from all subscriptions to prevent memory leaks.
     */
    disconnectedCallback() {
        this.subscriptions.unsubscribeAll();
    }
    render() {
        return (h(Host, { key: 'e804e5e5580a95b69639645bfab907601c058c61', class: {
                'wb-layout': true,
                'is-embedded': this.isEmbedded,
                'hide-navbar': !this.showNavbar
            } }, h("div", { key: '3c9a99e6e75c49abc8857ca3a4cd1cab647ecf34', class: "default-slot-wrapper" }, h("slot", { key: 'e17a4cf9e5d9f4964ea4e298ff2f35c10acab3ef', name: "default" })), !this.isEmbedded &&
            h("header", { key: '969dec650ec39d56bc570f37bcb8e324b94bded5', class: "wb-header" }, this.showHeader && h("onto-header", { key: '431a2437aa740076630e14f3f3548efc9cd3ddb9' })), !this.isEmbedded && this.showNavbar &&
            h("nav", { key: '8c901178a258aeb6f06f189fb49124d7b7601d03', class: "wb-navbar" }, h("onto-navbar", { key: 'ffd590a345d930bb64140426890a7a5f0f52f387', ref: this.assignNavbarRef(), "navbar-collapsed": this.isLowResolution })), h("div", { key: '25b14fd7973ca79d667d0877146bc053d8788263', class: { 'main-slot-wrapper': true, 'loading': this.loading } }, this.loading && h("div", { key: 'bf6cb9e06407ca99fb3ee7cbe096ab0964cf766e', class: "loader-wrapper" }, h("onto-loader", { key: 'e441c8d8a0a22aaab0028bc081b5df00ffb9d7b0', size: 96, messageText: TranslationService.translate('common.loading') })), h("slot", { key: '6738aeda481d1c26d1777284123ec1e794d81726', name: "main" })), !this.isEmbedded &&
            h("footer", { key: '1af8f2c5be2af29fa602774e4caeac6ef0354b79', class: "wb-footer" }, h("onto-footer", { key: 'e93aac557e306d05e6b8e143f6baaee877ab1c33' })), h("onto-tooltip", { key: '80e5775e95c10abca123c4c910e3d810ef53c230' }), h("onto-confirm-cancel-dialog", { key: 'e534a95889e7cc7230d973bad6d69acb2945369e' })));
    }
    // ========================
    // Event Listeners
    // ========================
    /**
     * Event listener for the navbar toggled event. The layout needs to respond properly when the navbar is toggled in
     * order to fit the content.
     * @param event
     */
    onNavbarToggled(event) {
        this.isNavbarCollapsed = event.detail.payload;
        if (this.isNavbarCollapsed) {
            this.hostElement.classList.add('expanded');
        }
        else {
            this.hostElement.classList.remove('expanded');
        }
    }
    /**
     * Event listener for window resize event. We need to handle this in order to allow the navbar to collapse when the
     * window is resized.
     */
    onResize() {
        this.windowResizeObserver();
    }
    // ========================
    // Handlers
    // ========================
    windowResizeHandler() {
        var _a, _b, _c, _d;
        this.isLowResolution = WindowService.getWindow().innerWidth <= WINDOW_WIDTH_FOR_COLLAPSED_NAVBAR;
        const navbar = document.querySelector('.navbar-component');
        if (navbar) {
            const cookieBanner = document.querySelector('.cookie-consent-banner');
            const header = document.querySelector('.header-component');
            const headerHeight = (_b = (_a = header === null || header === void 0 ? void 0 : header.getBoundingClientRect()) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : 0;
            const bannerHeight = (_d = (_c = cookieBanner === null || cookieBanner === void 0 ? void 0 : cookieBanner.getBoundingClientRect()) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 0;
            navbar.style.height = `calc(100% - ${headerHeight}px - ${bannerHeight}px)`;
        }
        if (!this.isLowResolution && !this.isNavbarCollapsed) {
            this.hostElement.classList.remove('expanded');
        }
        else {
            this.hostElement.classList.add('expanded');
        }
    }
    handleAuthChange(changedKey) {
        var _a;
        if (!changedKey) {
            return;
        }
        if (this.authEvents.includes(changedKey)) {
            const authenticated = this.authStorageService.isAuthenticated();
            if (authenticated) {
                navigate((_a = this.navigationContextService.getPreviousRoute()) !== null && _a !== void 0 ? _a : './');
            }
            else {
                navigate('login');
            }
            WindowService.getWindow().location.reload();
        }
    }
    // ========================
    // Security & Permissions
    // ========================
    subscribeToSecurityChanges() {
        const securityContextService = service(SecurityContextService);
        this.subscriptions.add(securityContextService.onAuthenticatedUserChanged((authenticatedUser) => {
            this.authenticatedUser = authenticatedUser;
            this.updateVisibility();
        }));
        this.subscriptions.add(securityContextService.onSecurityConfigChanged((securityConfig) => {
            this.securityConfig = securityConfig;
            this.updateVisibility();
        }));
        this.subscriptions.add(this.eventService.subscribe(EventName.LOGIN, () => {
            this.setNavbarItemVisibility();
            this.updateVisibility();
        }));
        this.subscriptions.add(this.eventService.subscribe(EventName.LOGOUT, () => {
            this.setNavbarItemVisibility();
            this.updateVisibility();
            navigate('login');
        }));
    }
    subscribeToRuntimeConfigurationChanges() {
        this.subscriptions.add(this.runtimeConfigurationContextService.onRuntimeConfigurationChanged((config) => {
            var _a;
            this.isEmbedded = (_a = config === null || config === void 0 ? void 0 : config.isEmbedded) !== null && _a !== void 0 ? _a : false;
        }));
    }
    updateVisibility() {
        if (!this.authenticationService.isSecurityEnabled()) {
            this.showNavbar = true;
            this.showHeader = true;
        }
        else {
            this.showHeader = this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
            this.showNavbar = this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
        }
    }
    isAuthenticatedFully() {
        return !this.authenticationService.isSecurityEnabled() || this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
    }
    shouldShowMenu(role) {
        return this.isAuthenticatedFully() && this.authorizationService.hasRole(role);
    }
    // ========================
    // Navbar logic
    // ========================
    assignNavbarRef() {
        return (navbar) => {
            this.navbarRef = navbar;
            if (!this.navbarRef) {
                return;
            }
            this.navbarRef.menuItems = WindowService.getPluginRegistry().get(MainMenuExtensionPoint.NAME);
            this.setNavbarItemVisibility();
        };
    }
    setNavbarItemVisibility() {
        var _a, _b, _c;
        if (!((_a = this.navbarRef) === null || _a === void 0 ? void 0 : _a.menuItems)) {
            return;
        }
        // recursively check for children and set their shouldShow property
        const processItem = (item) => {
            var _a;
            item.shouldShow = this.shouldShowMenu(item.role);
            if ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) {
                item.children.forEach(processItem);
            }
        };
        (_c = (_b = this.navbarRef) === null || _b === void 0 ? void 0 : _b.menuItems) === null || _c === void 0 ? void 0 : _c.forEach(plugin => {
            plugin.items.forEach(processItem);
        });
        // Update the reference to trigger a re-render
        this.navbarRef.menuItems = [...this.navbarRef.menuItems];
    }
    subscribeToNavigationEnd() {
        this.subscriptions.add(this.eventService.subscribe(EventName.NAVIGATION_END, (navigationEndPayload) => {
            this.navigationContextService.updatePreviousRoute(navigationEndPayload.oldUrl);
        }));
    }
    subscribeToApplicationChange() {
        this.subscriptions.add(this.applicationLifecycleContextService.onApplicationStateBeforeChange((state) => {
            if (state) {
                this.loading = true;
            }
        }));
        this.subscriptions.add(this.applicationLifecycleContextService.onApplicationStateChange((state) => {
            if (state) {
                this.loading = false;
            }
        }));
    }
    get hostElement() { return getElement(this); }
};
OntoLayout.style = ontoLayoutCss;

export { OntoLayout as onto_layout };
